import { Injectable, effect, inject, EventEmitter, Injector, runInInjectionContext } from '@angular/core';
import { BaseDataService, OfflineModeService, StorageService, TransformationService, MigrationService, initialWorkflow, PointStatus, SaveDocumentOptions, effectiveStatus, diffDocFields, appendEditAudit, BATCH_EDIT_FIELDS,
	ConsoleErrorService, GRUPPEN_ZUORDNUNG_NUR_ONLINE, mitGruppenDifferenz, mitNeuemGruppenStempel,
} from '@luzula/luzula-lib';
import { gehoertZuProjekt } from './bohrpunkte/detail/point-project-membership';
import { AufnahmePunktDocument, setPunktartList, PunktartRow, frischeZeilenKennungen, neueZeilenKennung } from './data-model';
import { richtePunktNachProjektAus } from './bohrpunkte/verfahren/punkt-ausrichten';
import { MitVerfahren } from './bohrpunkte/verfahren/verfahren';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from './auth.service';
import { ProjectService } from './project.service';
import { DatabaseConfigService } from './database-config.service';
import { seedMigrationsDocument } from './migrations';
import { setBodenartenTabelle } from './bohrpunkte/evaluators/nwsk';
import { setFeinbodenformSubstratgruppen } from './bohrpunkte/evaluators/substratgruppe';
import { registriereFilzKatalog } from './bohrpunkte/filz-tiefen';
import { FilzKatalogZeile } from './bohrpunkte/filz-bodenform';
import { ListenService, BodenartEntry } from './listenverwaltung/listen.service';
import { OptionService } from './bohrpunkte/option.service';
import { bohrpunktKonfliktVergleich } from './bohrpunkte/bohrpunkt-konflikt-vergleich';
import { simpleListValue, listValuesForRuntime, type SimpleListItem } from './listenverwaltung/simple-list';
import { LISTEN_MIT_ERKLAERUNG, eintraegeMitAnzeige } from './listenverwaltung/liste-mit-erklaerung';
import { computeControlEvaluation, controlEvalPointsFromIndexRows, ControlEvaluationResult } from './bohrpunkte/detail/control-evaluation/control-evaluation';

// Re-export SyncProgress from BaseDataService
export type { SyncProgress } from '@luzula/luzula-lib';

// Filter-Funktionen für benutzerdefinierte Listen (werden von Komponenten verwendet)
export function hauptbodenformFilter(type: string, i = 0) {
	return (value: string, row: any) => {
		// if (idOf(type) === 7 && i === 0) return value.endsWith('M');
		return true;
	};
}

export function feinbodenformFilter(hbf: string) {
	return (value: string, row: any) => {
		if (!hbf) return true;
		if (!row || !row['HBF']) return value === hbf;
		return row['HBF'].trim() === hbf;
	};
}

/**
 * Das Wuchsgebiet darf fehlen (am Punkt ist es eine freiwillige Angabe): Dann
 * wird nichts ausgefiltert. Die Funktion hat das schon immer so gehalten; die
 * Signatur verlangte trotzdem einen Wert.
 */
export function wuchsbezirkFilter(wg?: string) {
	return (value: string, row: any) => {
		if (!wg) return true;
		if (!row || !row['WG']) return value === wg;
		return row['WG'].trim() === wg;
	};
}

// Hinweis: Die frueheren Konstanten EDITABLE_LISTS und STORAGE_PREFIX (LocalStorage-
// Listen) sind mit der Umstellung auf den zentralen ListStore entfallen. Einzige
// autoritative Liste-der-Listen ist jetzt ListenService.editableLists (Registry).

/**
 * DataService für Bohrpunkte-Verwaltung.
 *
 * Erbt alle generische Funktionalität von BaseDataService:
 * - CRUD: get, getAll, put, bulkGet, bulkDocs, remove, removeBulk
 * - Query: queryIndex, getDocument$, saveDocument
 * - Download: downloadDocuments, downloadDocumentsByIds, ensureEmptyOfflineDatabase
 * - Sync: syncToRemote, syncCurrentProjectToRemote, checkinBohrpunkte, exitOfflineMode
 * - Views: storeViews, getView, getFilter
 * - HardData: getHardcoded, loadCustomLists, reloadCustomList
 * - Offline: discardDownloadedPoints, arePointsDownloaded, etc.
 *
 * App-spezifische Logik ist in BohrpunktDocumentHandler implementiert.
 */
@Injectable({
	providedIn: 'root'
})
export class DataService extends BaseDataService<AufnahmePunktDocument> {
	private injector = inject(Injector);
	private ces = inject(ConsoleErrorService);
	private dbConfig = inject(DatabaseConfigService);
	private _storage = inject(StorageService);
	private _oms = inject(OfflineModeService);
	private _ps = inject(ProjectService);
	private _msg = inject(ToastrService);
	private _ts = inject(TransformationService);
	private migrationService = inject(MigrationService);
	private auth = inject(AuthService);

	/**
	 * Holt die Anmeldung nach, bevor die Bibliothek zu übertragen versucht.
	 *
	 * Der Abgleich fragt hier an, wenn die Verbindung auf dem öffentlichen
	 * Nur-Lese-Weg steht (dort weist der Gateway jedes Schreiben mit `405` ab).
	 * Aus dem Offline-Modus heraus reicht die gewöhnliche Erneuerung nicht: Da
	 * lief `keycloak.init()` nie, es gibt also gar nichts zu erneuern. Deshalb
	 * geht es über `holeAnmeldungNach()`, das den Unterbau erst hochfährt.
	 */
	protected override async authRefresh(): Promise<boolean> {
		return this.auth.holeAnmeldungNach();
	}

	/** Zentraler Listen-Store (lazy via Injector, vermeidet Konstruktor-Zyklen). */
	private _listen?: ListenService;
	private get listen(): ListenService {
		return (this._listen ??= this.injector.get(ListenService));
	}

	/** OptionService (lazy) — für store-abhängige Validierungen (Humusform-Regex). */
	private _option?: OptionService;
	private get option(): OptionService {
		return (this._option ??= this.injector.get(OptionService));
	}

	/**
	 * Feldpfad → Klartext-Label aus den Batch-Edit-Feldmetadaten (lazy).
	 * Quelle der Beschriftung für die Kontrolleur-Audit-Historie.
	 */
	private _auditFieldLabels?: Map<string, string>;
	private get auditFieldLabels(): Map<string, string> {
		return (this._auditFieldLabels ??= new Map(
			this.injector.get(BATCH_EDIT_FIELDS).map(f => [f.key, f.label] as const)
		));
	}

	// Service-Getter für BaseDataService
	protected override get storage() { return this._storage; }
	protected override get oms() { return this._oms; }
	protected override get ps() { return this._ps; }
	protected override get msg() { return this._msg; }
	protected override get ts() { return this._ts; }

	// ========== BasePouchDbService Abstract Implementations ==========

	override get remoteDbName(): string {
		return this.dbConfig.punkteDbName;
	}

	override get localDbName(): string {
		// Lokale DB nutzt Projekt-ID als Namen
		return this.ps.project() || '';
	}

	protected override get projectSignal() {
		return this.ps.project;
	}

	protected override get userOfflineModeSignal() {
		return this.oms.userOfflineMode;
	}

	protected override get networkOnlineSignal() {
		return this.oms.networkOnline;
	}

	protected override async onInitializeRemoteDatabase(db: PouchDB.Database): Promise<void> {
		await Promise.all([
			this.ensureApplicationDesignDocument(db, true),
			seedMigrationsDocument(db)
		]);
		await this.migrationService.initialize();
	}

	protected override async onInitializeLocalDatabase(db: PouchDB.Database): Promise<void> {
		await this.ensureApplicationDesignDocument(db);
		// Migrationen auch im Offline-Modus laden (initialize() hat Guard gegen Doppelaufruf)
		await this.migrationService.initialize();
	}

	// ========== Kontrolleur-Feld-Audit (G7) ==========

	/**
	 * Top-Level-Felder, die NICHT als fachliche Datenänderung auditiert werden
	 * (Meta-/Workflow-/Kommentar-/Sync-Felder).
	 *
	 * `state` ist die abgeleitete Validierungs-Zustands-Struktur (commentsSeverity,
	 * hasDiskussions, resolved …). Sie wird automatisch aus `checkerComments`
	 * berechnet und ändert sich als Nebenprodukt, sobald ein Prüfer einen Kommentar
	 * setzt — kein eigenständiges „korrigiertes Feld". Daher ausgeschlossen, damit
	 * die Historie kein `state.*`-Rauschen mehr anzeigt (bereits gespeicherte
	 * Alt-Einträge werden in der Anzeige via editFieldLabel/editValueText lesbar
	 * gemacht).
	 */
	private static readonly AUDIT_EXCLUDE = new Set<string>([
		'_id', '_rev', '_attachments', '_conflicts',
		'lastModified', 'workflow', 'checkerComments', 'batchEditHistory', 'state'
	]);

	/**
	 * Save-Override mit Kontrolleur-Feld-Audit (G7): Speichert ein Kontrolleur
	 * Datenfelder eines Punkts in `in_pruefung`/`abgelehnt` (Korrektur-Modus,
	 * Phase J — erst nach Annahme bzw. nach Ablehnung), wird vor dem Persist
	 * pro geändertem Feld automatisch ein `WorkflowEditEntry` an die
	 * Workflow-History angehängt. Für alle anderen Rollen/Stati ist das ein
	 * No-op — der reguläre Bearbeiter-Save bleibt unverändert.
	 */
	override async saveDocument(doc: any, options?: SaveDocumentOptions): Promise<boolean> {
		await this.recordKontrolleurAuditIfNeeded(doc, options?.user);
		return super.saveDocument(doc, options);
	}

	private async recordKontrolleurAuditIfNeeded(doc: AufnahmePunktDocument, user?: string): Promise<void> {
		try {
			// Nur wenn der aktuelle User die (reine) Kontrolleur-Rolle einnimmt —
			// Entwickler editieren ohne Audit (admin-Bypass). Äquivalent zum früheren
			// `getWorkflowRole(...) === 'kontrolleur'` (das entwickler→'admin' vorzog),
			// aber ohne den deprecateten Single-Role-Kollaps (MR, 2026-06-18).
			if (this._ps.canIbe('entwickler') || !this._ps.canIbe('kontrolleur')) return;
			const status = effectiveStatus(doc);
			// Phase J: Kontrolleur korrigiert erst nach Annahme (in_pruefung) bzw.
			// nach Ablehnung (abgelehnt) — deckungsgleich mit canEditPoint.
			if (status !== 'in_pruefung' && status !== 'abgelehnt') return;

			const db = this.db();
			if (!db) return;
			let old: AufnahmePunktDocument | undefined;
			try {
				old = await db.get(doc._id) as AufnahmePunktDocument;
			} catch {
				return; // Punkt (noch) nicht in DB — nichts zu vergleichen.
			}

			const changes = diffDocFields(old as any, doc as any, DataService.AUDIT_EXCLUDE);
			if (changes.length === 0) return;

			// Geänderte Felder mit Klartext-Labels aus den Batch-Edit-Feldmetadaten
			// versehen (z.B. 'header.nummer' → „Bohrpunkt-Nummer"), damit die Historie
			// lesbar ist. Felder ohne Katalog-Eintrag bleiben beim technischen Pfad —
			// bewusst kein geratenes Label für unbekannte Kürzel.
			const labels = this.auditFieldLabels;
			for (const c of changes) {
				const label = labels.get(c.field);
				if (label) c.fieldLabel = label;
			}

			appendEditAudit(doc, changes, { user: user ?? this.auth.currentUser });
		} catch (e) {
			// Audit darf den Save nie verhindern.
			// Der Save darf nicht scheitern — ein Loch in der PRUEFSPUR ist aber
			// etwas anderes als ein verpasster Komfort: Hinterher laesst sich nicht
			// mehr sagen, wer was geaendert hat.
			console.warn('[Kontrolleur-Audit] übersprungen:', e);
			this.ces.melde('[Audit] Kontrolleur-Aenderung nicht protokolliert', e);
		}
	}

	protected override onDatabasesInitialized(): void {
		this.isInitialized = true;
		this.initialized.emit();
		// currentDb_ nur emittieren wenn eine echte DB verfügbar ist
		if (this.db()) {
			this.currentDb_.next(this.db()!);
		}
		// Einmal vollständig nachsehen, von welchen Punkten zwei Fassungen
		// bestehen. Ohne das kannte der Hinweis über der Hauptansicht nur, was
		// gerade durch einen Abgleich lief: Nach einem Neustart stand er auf
		// null, obwohl auf dem Server weiter zwei Fassungen lagen, und geklärt
		// wurde zufällig das, was vorbeikam.
		//
		// Hier und nicht öfter: Diese Stelle läuft beim Start, beim
		// Projektwechsel und beim Wechsel zwischen unterwegs und im Netz. Die
		// Suche selbst kostet zwei sparsame Abfragen (erst nur Kennungen und
		// Revisionen, Inhalte nur für die Treffer).
		void this.pruefeAlleAufZweiFassungen().catch(err => {
			console.warn('[Konflikte] Vollständige Suche beim Start übersprungen:', err);
			this.ces.melde('[Konflikte] Vollstaendige Suche uebersprungen', err);
		});
	}

	/**
	 * Der Feldvergleich für das stille Aufräumen zweier Fassungen.
	 *
	 * Mehr ist hier nicht zu tun: Wann aufgeräumt wird (nach der vollständigen
	 * Suche und gebündelt im Dauerabgleich), regelt die Bibliothek. Sie weiß nur
	 * nicht, welche Felder bpweb selbst führt — das steht in
	 * {@link bohrpunktKonfliktVergleich}.
	 *
	 * Anlass: Am 10.09.2026 meldete der Balken 533 Datensätze, und im
	 * Klär-Fenster stand keine einzige beantwortbare Frage.
	 */
	protected override konfliktVergleichZumAufraeumen() {
		return bohrpunktKonfliktVergleich();
	}

	/**
	 * Zwei Fassungen darf hier nur klaeren, wer den Punkt auch sonst bearbeiten
	 * duerfte (Nutzer-Entscheidung 11.09.2026: "nur was ich im aktiven Projekt
	 * auch duerfte").
	 *
	 * **Warum das noetig war:** Es gibt EINE Punkt-Datenbank fuer die ganze App,
	 * nicht je Projekt. Die Suche nach zwei Fassungen ging darueber hinweg und
	 * stempelte jedem Fund das aktive Projekt auf. Im Klaer-Fenster standen
	 * dadurch Punkte aus fremden Projekten - "das sind keine Konflikte, die ich
	 * ueberhaupt aufloesen duerfte!". Seit dem 10.09.2026 schrieb das stille
	 * Aufraeumen dort sogar ohne Rueckfrage.
	 *
	 * `canIWritePoint` ist dasselbe Tor wie beim gewoehnlichen Speichern und
	 * deckt alles ab: Schreibrecht am Projekt, Projekt-Lock, Frozen-Lock,
	 * Cross-Projekt-Schutz und das Status-/Rollen-Tor (PERMISSIONS.md 3.6).
	 */
	protected override darfKonfliktKlaeren(doc: AufnahmePunktDocument): boolean {
		return this._ps.canIWritePoint(doc as any);
	}

	protected override onDatabasesClosed(): void {
		// DataService hat keine spezielle Logik beim Schließen
	}

	protected override async shouldKeepEmptyLocalDb(dbName: string): Promise<boolean> {
		const wasDownloaded = await this.storage.getProjectValue('pointsDownloaded', dbName);
		return this.oms.userOfflineMode() || wasDownloaded === true;
	}

	protected override onConnectionFailed(): void {
		// Events werden von BaseDataService verwaltet
		// connectionFailed.emit() wird in BaseDataService aufgerufen
	}

	protected override onConnectionSuccess(message?: string): void {
		// Events werden von BaseDataService verwaltet
		// connected.emit() wird in BaseDataService aufgerufen
	}

	// ========== End BasePouchDbService Implementations ==========

	constructor() {
		super();

		// MigrationService mit DB-Getter verbinden
		this.migrationService.setRemoteDbGetter(() => this.remoteDb ?? null);

		// Listen-Store (Single Source) anbinden: store-abhängige Seiteneffekte
		// (nWSK-BodenartenTabelle + Humusform-Validierungsregex) aus dem Store ziehen,
		// sobald hydriert, und bei jeder Store-Änderung (Pull / Editor-Save) nachziehen.
		void this.listen.ready().then(() => this.applyStoreDerived());
		this.listen.watchChanges().subscribe(() => this.applyStoreDerived());

		// WICHTIG: Warte auf ProjectService.initialized, damit das Projekt aus Storage geladen wurde.
		// Sonst initialisiert sich DS zweimal (ohne/mit Projekt) → doppeltes Laden, Karten-Flicker.
		this._ps.initialized.subscribe(() => {
			runInInjectionContext(this.injector, () => this._initDatabases());
		});

		// App-spezifischer Effect: _documentsDownloaded Status bei Projektwechsel aktualisieren
		effect(async () => {
			const projectId = this.ps.project();

			// Bei Projektwechsel: Status zurücksetzen
			this._documentsDownloaded = false;

			// Wenn lokale DB verfügbar ist: Status wiederherstellen
			if (this.localDb && projectId) {
				try {
					const info = await this.localDb.info();
					if (info.doc_count > 0) {
						this._documentsDownloaded = true;
					} else {
						const wasDownloaded = await this.storage.getProjectValue('pointsDownloaded', projectId);
						if (wasDownloaded === true) {
							this._documentsDownloaded = true;
						}
					}
				} catch (err) {
					// Antwortet die lokale Datenbank hier nicht, ist das derselbe
					// Zustand, der im September 2026 den Start blockierte.
					console.warn('[DataService] Fehler beim Prüfen von _documentsDownloaded:', err);
					this.ces.melde('[Offline-Stand] Lokale Datenbank nicht lesbar', err);
				}
			}
		});
	}

	// ========== Wrapper-Methoden für Rückwärtskompatibilität ==========

	// Events (zusätzlich zu denen aus BaseDataService)
	nummerChanged = new EventEmitter<{ id: string, num: string}>();

	// hardData für direkten Zugriff (Komponenten nutzen das)
	get hardData(): any {
		const map = this.documentHandler.getHardData();
		const obj: any = {};
		map.forEach((value, key) => {
			obj[key] = value;
		});
		return obj;
	}

	// Wrapper: updateBohrpunkt für Komponenten (ruft transformAfterLoad auf)
	updateBohrpunkt(bp: any, fetchWmsData = true): any {
		bp = this.documentHandler.transformAfterLoad(bp);
		if (fetchWmsData) {
			this.documentHandler.enrichDocument(bp);
		}
		return bp;
	}

	// Wrapper: removeBohrpunkt → remove
	async removeBohrpunkt(id: string) {
		return this.remove(id);
	}

	// Wrapper: removeBohrpunkteBulk → removeBulk
	async removeBohrpunkteBulk(ids: string[]) {
		return this.removeBulk(ids);
	}

	// Wrapper: checkoutBohrpunkte → downloadDocuments
	async checkoutBohrpunkte(bnds?: any[], filterName?: string|null): Promise<void> {
		return this.downloadDocuments(bnds, filterName);
	}

	// Wrapper: checkoutEmpty → ensureEmptyOfflineDatabase
	async checkoutEmpty(): Promise<void> {
		return this.ensureEmptyOfflineDatabase();
	}

	// Wrapper: addBohrpunkte → bulkDocs
	async addBohrpunkte(bpDocs: any[]) {
		return this.bulkDocs(bpDocs);
	}

	// Wrapper: getBohrpunkteBatch → bulkGet
	async getBohrpunkteBatch(ids: string[]): Promise<any[]> {
		return this.bulkGet(ids);
	}

	/**
	 * Das Kartierverfahren des geöffneten Projekts.
	 *
	 * Gelesen aus den Projektwerten auf dem Gerät, nicht aus der Projektliste: Die
	 * Liste entsteht online aus einer schlanken Sicht ohne dieses Feld, die
	 * Projektwerte dagegen tragen alle Felder des Projekt-Dokuments und stehen auch
	 * im Offline-Modus bereit. Ohne geöffnetes Projekt kommt `undefined` zurück,
	 * und das heißt SEA 95.
	 */
	private async verfahrenDesProjekts(): Promise<MitVerfahren> {
		return {
			verfahren: await this._ps.getProjectValue('verfahren'),
			kaAuflage: await this._ps.getProjectValue('kaAuflage'),
		};
	}

	// Wrapper: createBohrpunktFrom (DEPRECATED, aber für Übergangsphase)
	async createBohrpunktFrom(bp: AufnahmePunktDocument, options?: { keepHorizonte?: boolean, keepSubstrate?: boolean, keepVegetationen?: boolean }) {
		const newDoc = this.documentHandler.createNew(bp);
		// Übernommene Zeilen bekommen FRISCHE Kennungen: Sie gehören ab jetzt zu
		// einem eigenen Datensatz. Dieselbe Kennung in zwei Punkten wäre eine
		// Falle für alles, was Zeilen über ihre Kennung wiederfindet — unter
		// anderem die Klärung zweier Fassungen.
		if (!options?.keepHorizonte) newDoc.Horizontfolge = [];
		else newDoc.Horizontfolge = frischeZeilenKennungen(newDoc.Horizontfolge);
		if (!options?.keepSubstrate) newDoc.Substratfolge = [];
		else newDoc.Substratfolge = frischeZeilenKennungen(newDoc.Substratfolge);
		if (!options?.keepVegetationen) newDoc.bodenvegetation = { aufnahme: { schichtAnteile: {}, vegetationen: [] } };
		else if (newDoc.bodenvegetation?.aufnahme) {
			newDoc.bodenvegetation.aufnahme.vegetationen =
				frischeZeilenKennungen(newDoc.bodenvegetation.aufnahme.vegetationen);
		}
		newDoc.header.bemerkungen = '';
		delete (newDoc as any)._id;
		delete (newDoc as any)._rev;

		// Kartierverfahren: Das PROJEKT bestimmt, was ein neuer Punkt ist. In einem
		// SEA-Projekt (also in jedem bisherigen) ändert das nichts am Datensatz; in
		// einem KA-Projekt bekommt der Punkt den Vermerk und seine KA-Aufnahme.
		// Trägt das Projekt ein Verfahren, das diese Fassung nicht kennt, wird NICHTS
		// angelegt: Der Punkt ließe sich hinterher nicht mehr richtig lesen.
		try {
			richtePunktNachProjektAus(newDoc, await this.verfahrenDesProjekts(), {
				horizonteBehalten: !!options?.keepHorizonte,
				neueKennung: neueZeilenKennung,
			});
		} catch (fehler) {
			this._msg.error((fehler as Error).message, 'Punkt nicht angelegt');
			throw fehler;
		}

		// Workflow-Block initialisieren — Status 'bearbeitung', createdBy = aktueller User.
		// Beim Klonen eines bestehenden Punkts wird createNew() den fremden workflow-Block
		// mit-übertragen; den überschreiben wir hier bewusst, damit der duplizierte Punkt
		// als neue Bearbeitung beim Klonenden anfängt.
		// `projectId` = aktives Projekt: ohne sie greift der Cross-Projekt-Schutz
		// (canSeePoint/canEditPoint via belongsToOtherProject) für neue Punkte NICHT,
		// weil er nur workflow.projectId auswertet — symmetrisch zu Migration
		// (header.projects[0] → projectId) und Übernahme (restartWorkflow). Ohne
		// aktives Projekt (''/undefined) bleibt der Punkt projektlos (Bestand-artig).
		(newDoc as any).workflow = initialWorkflow({
			user: this.auth.currentUser,
			userId: this.auth.currentUserId,
			projectId: this._ps.project() || undefined
		});

		// Gruppen-Stempel beim Anlegen (30.09.2026): Hier stand ein rohes `post`, an
		// `prepareDocumentForSave` vorbei — der neue Punkt ging OHNE Gruppen hinaus. Online
		// schreibt bpweb direkt auf den Server, und der Gateway weist ein Dokument ohne
		// Gruppen ab (403): Ein normaler Nutzer konnte online keinen Punkt anlegen. Offline
		// entstand er ungestempelt und blieb beim Hochladen haengen. Der Punkt gehoert per
		// `workflow.projectId` zum aktiven Projekt und bekommt dessen Gruppen; die der
		// Vorlage (beim Kopieren) kopiert `createNew` nicht mit.
		const zuSchreiben = this.prepareDocumentForSave(newDoc);
		if (Array.isArray(zuSchreiben.groups)) (newDoc as any).groups = [...zuSchreiben.groups];
		const result = await this.db()?.post(zuSchreiben);
		if (result) {
			(newDoc as any)._id = result.id;
			(newDoc as any)._rev = result.rev;
			this.pointSaved.emit(newDoc);
			if (this.oms.userOfflineMode()) {
				// Ueber die Basisklasse, damit der Vermerk ein Neuladen ueberlebt.
				this.setzeOffeneAenderungen(true);
			}
		}
		return result;
	}

	// Override discardLocal als public
	override async discardLocal() {
		await super.discardLocal();
	}

	// ========== App-Spezifische Hilfsmethoden ==========

	/**
	 * Liefert Lookup-Daten für Dropdowns/Typeaheads. Single Source: bekannte
	 * Listen kommen aus dem zentralen ListStore (DB → Replik → Seed), alle
	 * übrigen (noch nicht migrierten) Tabellen aus den gebündelten HardData.
	 */
	public override getHardcoded(table: string, func?: (row: any) => boolean): any[] {
		if (this.listen.isKnownList(table)) {
			// simple-Listen koennen pro Eintrag eine Erklaerung tragen
			// ({ wert, erklaerung }); Formulare/Dropdowns bekommen weiterhin reine
			// Werte (listValuesForRuntime laesst andere Typen unveraendert).
			const type = this.listen.getListConfig(table)?.type ?? '';
			let data = listValuesForRuntime(type, this.listen.getSync(table) ?? []);
			// Punktarten: abgeleitetes Anzeige-Label "Beschreibung (Nummer)" fuer die
			// Typeaheads (Punktart/PunktartMitProtokoll, tableColumn:'label'). Nur fuer
			// Dropdowns — der Editor (getList/getSync) bekommt das Feld NICHT.
			if (table === 'Punktarten') {
				data = data.map((r: any) => ({
					...r,
					label: r?.beschreibung ? `${r.beschreibung} (${r.nummer})` : String(r?.nummer ?? ''),
				}));
			}
			// Listen, deren Auswahlfeld auch den Klartext zeigen soll (Mesoklima):
			// Wert und Anzeige getrennt, damit weiter das Kuerzel gespeichert wird.
			// Gebraucht wird dafuer die ROHE Liste — listValuesForRuntime hat die
			// Erklaerungen oben schon weggeworfen.
			if (LISTEN_MIT_ERKLAERUNG.has(table)) {
				data = eintraegeMitAnzeige((this.listen.getSync(table) ?? []) as SimpleListItem[]);
			}
			return func ? data.filter(func) : data;
		}
		return super.getHardcoded(table, func);
	}

	/**
	 * True, wenn `table` eine im zentralen ListStore registrierte Liste ist.
	 * Vom ListDataProvider genutzt, um auch `from:'dietmarsMdb'`-Tabellen
	 * (sofern migriert) aus dem Store statt per HTTP (MdbService) zu liefern.
	 */
	public isListKnown(table: string): boolean {
		return this.listen.isKnownList(table);
	}

	/**
	 * Lädt eine einzelne Liste neu. Single Source: die Daten liegen bereits im
	 * zentralen ListStore (nach dem Editor-Save ist dessen Cache aktuell) — hier
	 * wird nur der nWSK-Seiteneffekt der BodenartenTabelle nachgezogen.
	 * Wird vom Editor nach dem Speichern aufgerufen.
	 */
	public override async reloadCustomList(listName: string) {
		if (listName === 'BodenartenTabelle') {
			this.applyCustomBodenartenTabelle();
		}
	}

	/** Wendet store-abhängige Seiteneffekte an (nWSK-BodenartenTabelle,
	 *  Humusform-Regex, Punktarten-Lookup, Substratgruppe je Feinbodenform). */
	private applyStoreDerived() {
		this.applyCustomBodenartenTabelle();
		// Humusformen ist eine simple-Liste -> auf reine Werte normalisieren, bevor
		// die Validierungs-Regex daraus gebaut wird (sonst [object Object]).
		this.option.setHumusFormList((this.listen.getSync('Humusformen') ?? []).map(simpleListValue));
		// Punktarten: Nummer/Beschreibung/SVG als Single Source ins data-model-Lookup
		// pushen (Karte/Liste/Legende lesen daraus). Reine String-Listen (vor der
		// genericTable-Migration) werden vom Setter ignoriert -> Fallback bleibt aktiv.
		setPunktartList(this.listen.getSync('Punktarten') as PunktartRow[]);
		// Substratgruppe je Feinbodenform: Die Angabe steht im Bodenformenkatalog an
		// der Hauptbodenform und wird beim Ableiten der Feinbodenformen-Liste
		// mitgenommen (Feld `STG`). Von hier aus belegt sie das Feld
		// "Substrat" der Stamm-Standortformengruppe vor.
		setFeinbodenformSubstratgruppen(
			this.listen.getSync('Feinbodenformen') as { FBF?: string; STG?: string }[],
		);
		// Filz-Bodenformen (filz-tiefen.ts): Die Regel fragt die Feinbodenformen-
		// Liste nach dem Klartext. Bei jeder Aenderung der Listen wird der
		// Nachschlag verworfen und beim naechsten Blick neu gebaut.
		registriereFilzKatalog(() => this.getHardcoded('Feinbodenformen') as FilzKatalogZeile[]);
	}

	/** Setzt die BodenartenTabelle (aus dem ListStore) für die nWSK-Berechnung. */
	private applyCustomBodenartenTabelle() {
		const arr = this.listen.getSync('BodenartenTabelle') as BodenartEntry[] | undefined;
		if (arr?.length) {
			setBodenartenTabelle(ListenService.bodenartenTabelleFromArray(arr));
		}
	}

	/**
	 * Liefert die Workflow-Status-Counts der Punkte des angegebenen Projekts.
	 * Liest die queryIndex-Rows (key[4].projects + key[9].status) und aggregiert.
	 * Wird vom ProjectPointsService fuer die Aggregat-Statistik im
	 * ProjectManager genutzt.
	 */
	async getPointStatusCountsForProject(projectId: string): Promise<Map<PointStatus | null, number>> {
		const counts = new Map<PointStatus | null, number>();
		if (!projectId) return counts;
		const response: any = await this.queryIndex();
		if (!response?.rows) return counts;
		for (const row of response.rows) {
			const projects = row.key?.[4]?.projects;
			if (!Array.isArray(projects)) continue;
			if (!projects.some((p: any) => p?.id === projectId)) continue;
			// „Bestand" abgeschafft (2026-06-16) → Punkte ohne Workflow-Status (key[9]
			// null/leer) zählen als `abgeschlossen` — konsistent mit `materializeLegacyBestand`
			// (Liste/Karte) und `deriveProjectStatus`. So zeigt der Workflow-Editor sie
			// nicht mehr als „Kein Status", sondern als abgeschlossen (Problem A, 2026-06-18).
			const status: PointStatus | null = row.key?.[9]?.status ?? 'abgeschlossen';
			counts.set(status, (counts.get(status) ?? 0) + 1);
		}
		return counts;
	}

	/**
	 * Liefert die komplette Kontroll-Auswertung eines Projekts (Punktstati +
	 * Kommentar-Statistik) für das Kontroll-Auswertungs-Modal.
	 *
	 * Anders als `getPointStatusCountsForProject` braucht die Kommentar-Zählung die
	 * `checkerComments` der Punkte (Schwere + offen/erledigt + Threads), die NICHT
	 * im schlanken Index-Key stehen. Deshalb ein einziger `queryIndex(true)`-Aufruf
	 * mit `include_docs` — EIN Request, kein Save-Loop (IP-Sperre-sicher). Die
	 * eigentliche Zählerei liegt in der reinen `computeControlEvaluation`. Gibt
	 * zusätzlich die Rohpunkte zurück, damit das Modal Drill-down-IDs rechnen kann.
	 */
	async getControlEvaluationForProject(projectId: string): Promise<ControlEvaluationResult> {
		if (!projectId) return { evaluation: computeControlEvaluation([]), points: [] };
		const response: any = await this.queryIndex(true);
		const points = controlEvalPointsFromIndexRows(response?.rows, projectId);
		return { evaluation: computeControlEvaluation(points), points };
	}

	/**
	 * Wie `getPointStatusCountsForProject`, aber liefert pro Status die Liste
	 * der Punkte mit `id` + `nummer` (für das Punkt-Listen-Modal im Workflow-
	 * Editor). Selbe Filter-Logik wie die Counts; Punkte werden alphabetisch
	 * nach Nummer sortiert.
	 */
	async getPointInfosForProject(projectId: string): Promise<Map<PointStatus | null, { id: string; nummer?: string; createdById?: string }[]>> {
		const map = new Map<PointStatus | null, { id: string; nummer?: string; createdById?: string }[]>();
		if (!projectId) return map;
		const response: any = await this.queryIndex();
		if (!response?.rows) return map;
		for (const row of response.rows) {
			const projects = row.key?.[4]?.projects;
			if (!Array.isArray(projects)) continue;
			if (!projects.some((p: any) => p?.id === projectId)) continue;
			// „Bestand" abgeschafft (2026-06-16) → Punkte ohne Workflow-Status (key[9]
			// null/leer) zählen als `abgeschlossen` — konsistent mit `materializeLegacyBestand`
			// (Liste/Karte) und `deriveProjectStatus`. So zeigt der Workflow-Editor sie
			// nicht mehr als „Kein Status", sondern als abgeschlossen (Problem A, 2026-06-18).
			const status: PointStatus | null = row.key?.[9]?.status ?? 'abgeschlossen';
			const nummer: string | undefined = row.key?.[4]?.nummer;
			// `workflow.createdBy.id` für den Owner-Filter im Workflow-Editor.
			const createdById: string | undefined = row.key?.[9]?.createdBy?.id;
			const list = map.get(status) ?? [];
			list.push({ id: row.id, nummer, createdById });
			map.set(status, list);
		}
		// Pro Status alphabetisch nach Nummer sortieren (de-DE).
		for (const list of map.values()) {
			list.sort((a, b) => (a.nummer ?? a.id).localeCompare(b.nummer ?? b.id, 'de'));
		}
		return map;
	}

	/**
	 * Gehört der Punkt zum aktiven Projekt? Die Bibliothek fragt das, bevor sie beim
	 * Speichern die Projektgruppen dazulegt (30.09.2026, PERMISSIONS.md §13): Ein
	 * Bestandspunkt oder ein Punkt eines anderen Projekts behält seine Gruppen — sonst
	 * machte ihn schon das automatische Nachtragen der Adresse beim Ansehen für die
	 * Gruppe des aktiven Projekts sichtbar.
	 */
	protected override gehoertZumAktivenProjekt(doc: any, projektId: string): boolean {
		return gehoertZuProjekt(doc, projektId);
	}

	/** Wie viele Punkte das Nachstempeln höchstens in einer Sendung schreibt. */
	static readonly STEMPEL_PAKET = 100;

	/**
	 * Stempelt die Punkte eines Projekts nach einer Gruppen-Zuordnung nach.
	 * Aufgerufen vom PROJECT_DOC_GROUP_STAMPER.
	 *
	 * Bewusst ROH über `this.db()` geschrieben, NICHT über saveDocument/bulkDocs:
	 * deren `prepareDocumentForSave` würde mit dem AKTIVEN Projekt stempeln — beim
	 * Zuordnen eines anderen Projekts also die falschen groups.
	 *
	 * Seit 30.09.2026 (vorher: jeden Punkt in EINER Sendung auf die Projektgruppen SETZEN):
	 * - **Nur die Differenz** (Nutzer-Entscheidung, `mitGruppenDifferenz`): Ein Punkt
	 *   kann zu mehreren Projekten gehören; das Setzen nahm ihm die Gruppe des anderen
	 *   Projekts, und ohne Rechte daran wies der Gateway die ganze Sendung ab. Ohne
	 *   `vorher` (ältere Aufrufer) wird gesetzt wie bisher.
	 * - **Nur, was sich ändert.** Jede unnötige Fassung ist eine Gelegenheit für zwei
	 *   Fassungen (Torgelow 29.09.2026: „Adress-Abfrage gegen Gruppen-Stempel").
	 * - **Auch Altpunkte**, die nur `workflow.projectId` tragen (`gehoertZuProjekt`).
	 * - **In Paketen**, und ein abgelehntes Paket hält die anderen nicht auf.
	 *   Gescheitertes wird am Ende geworfen — die Bibliothek meldet es.
	 * - **Nur online**: in die Kopie auf dem Gerät gestempelt, entstünden beim
	 *   Hochladen zwei Fassungen, an denen niemand gearbeitet hat.
	 */
	async stampeGroups(projectId: string, groups: string[], vorher?: string[]): Promise<number> {
		if (!projectId || !groups?.length) return 0;
		if (this.oms?.userOfflineMode?.()) throw new Error(GRUPPEN_ZUORDNUNG_NUR_ONLINE);
		const db = this.db();
		if (!db) return 0;
		// Frisch lesen: Mit einer Versionsnummer aus dem Zwischenspeicher lehnte
		// der Server das Schreiben still ab.
		this.invalidateQueryCache();
		const response: any = await this.queryIndex(true);
		const docs: any[] = (response?.rows ?? [])
			.map((r: any) => r.doc)
			.filter((d: any) => d && !d._deleted && gehoertZuProjekt(d, projectId));
		const geaendert = vorher
			? mitGruppenDifferenz(docs, groups, vorher)
			: mitNeuemGruppenStempel(docs, groups);
		if (!geaendert.length) return 0;

		let geschrieben = 0;
		let gescheitert = 0;
		let ersterFehler: unknown;
		for (let i = 0; i < geaendert.length; i += DataService.STEMPEL_PAKET) {
			const paket = geaendert.slice(i, i + DataService.STEMPEL_PAKET);
			try {
				const antworten: any[] = await db.bulkDocs(paket);
				for (const a of antworten ?? []) {
					if (a?.ok) geschrieben++;
					else { gescheitert++; ersterFehler ??= a; }
				}
			} catch (err) {
				gescheitert += paket.length;
				ersterFehler ??= err;
			}
		}
		this.invalidateQueryCache();
		if (gescheitert > 0) {
			const grund = (ersterFehler as any)?.message ?? (ersterFehler as any)?.reason ?? (ersterFehler as any)?.error;
			throw new Error(`${gescheitert} von ${geaendert.length} Punkten nicht nachgestempelt`
				+ (grund ? ` (${grund})` : ''));
		}
		return geschrieben;
	}

	/**
	 * Utility: Mapped einen Wert aus einer HardData-Tabelle anhand der ID.
	 * DEPRECATED: Sollte über getHardcoded() verwendet werden.
	 */
	public mapFromHardData(which: string, id: string): string | undefined {
		const table = this.getHardcoded(which);
		if (!table) return undefined;

		for (const data of table) {
			if (data.ID !== id) continue;
			for (const k of Object.keys(data)) {
				if (k !== 'ID') return data[k];
			}
		}

		return undefined;
	}
}
